import { useMemo } from "react";

import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Button, Stack, Typography } from "@mui/material";

import { InfoTooltip } from "../../../../Components/InfoTooltip/InfoTooltip";
import { AmountInCustomerCurrency, useAmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import HomePageCard from "../HomePageCard";
import { LoadingCard } from "../LoadingCard";

type CloudWasteCardProps = {
  anomaliesAtRisk: number;
  budgetsAtRisk: number;
  insightsRisk: number;
  commitmentsAtRisk: number;
  anomaliesLoading: boolean;
  budgetsLoading: boolean;
  insightsLoading: boolean;
  commitmentsLoading: boolean;
  costAtRiskInLast7Days: number;
};

export const CloudWasteCard = ({
  anomaliesAtRisk,
  budgetsAtRisk,
  insightsRisk,
  commitmentsAtRisk,
  anomaliesLoading,
  budgetsLoading,
  insightsLoading,
  commitmentsLoading,
  costAtRiskInLast7Days,
}: CloudWasteCardProps) => {
  const history = useHistory();

  const anomaliesAtRiskInCustomerCurrency = useAmountInCustomerCurrency(anomaliesAtRisk);
  const commitmentsAtRiskInCustomerCurrency = useAmountInCustomerCurrency(commitmentsAtRisk);

  const totalCostAtRisk = useMemo(
    () => anomaliesAtRiskInCustomerCurrency + budgetsAtRisk + insightsRisk + commitmentsAtRiskInCustomerCurrency,
    [anomaliesAtRiskInCustomerCurrency, budgetsAtRisk, insightsRisk, commitmentsAtRiskInCustomerCurrency]
  );

  if (anomaliesLoading || budgetsLoading || insightsLoading || commitmentsLoading) {
    return <LoadingCard title="Preventable cloud waste" />;
  }

  const title = (
    <InfoTooltip
      cursor="default"
      description="Total preventable cloud waste is a combination of insights awaiting action, active cost anomalies, budgets with a forecasted overrun and commitments that are at risk of not meeting their commitment amount."
    >
      <Typography variant="h4" color="text.primary">
        Preventable cloud waste
      </Typography>
    </InfoTooltip>
  );

  return (
    <HomePageCard title={title}>
      <Stack sx={{ p: 3, height: 161 }} justifyContent="space-between">
        <Stack sx={{ alignItems: "flex-start", mt: 2 }} spacing={2}>
          <Typography sx={{ fontSize: 44, fontWeight: 500 }}>
            <AmountInCustomerCurrency amount={totalCostAtRisk} dontConvert />
          </Typography>

          <Typography variant="caption" sx={{ color: "error.main", ml: 1, display: "flex", alignItems: "center" }}>
            <TrendingUpIcon sx={{ width: 12, height: 12, color: "error.main", mr: 0.25 }} />
            <AmountInCustomerCurrency amount={costAtRiskInLast7Days} dontConvert /> since last week
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 2, width: "fit-content" }}
          data-cy="cloud-waste-insights-btn"
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            history.push("./optimize/insights");
          }}
        >
          View all insights
        </Button>
      </Stack>
    </HomePageCard>
  );
};
